import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, Select, MenuItem, FormControl, InputLabel, Tabs, Tab,
  Avatar, IconButton, Tooltip, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Divider, Checkbox, Collapse } from '@mui/material';
import { Upload, Download, Refresh, Delete, CheckCircle, Error, Warning,
  FileDownload, CloudUpload, Assessment, Edit, Add, Save, Cancel,
  FilterList, SelectAll, Edit as BulkEdit } from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API = '/api';
const STATUS_COLORS = { synced:'#4CAF50', pending:'#FF9800', failed:'#F44336' };
const UPLOAD_STATUS_COLORS = { completed:'#4CAF50', partial:'#FF9800', failed:'#F44336', processing:'#2196F3' };
const PRIORITY_COLORS = { high:'#F44336', medium:'#FF9800', low:'#4CAF50' };

export default function MasterData() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const canUpload = isAdmin || user?.role === 'manager';
  const fileRef = useRef();

  const [tab, setTab] = useState(0);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [downloadHistory, setDownloadHistory] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [columns, setColumns] = useState([]);
  const [configOpen, setConfigOpen] = useState(false);
  const [configColumns, setConfigColumns] = useState([]);
  const [addColOpen, setAddColOpen] = useState(false);
  const [newCol, setNewCol] = useState({label:'',type:'text',required:false,options:''});
  const [configSaving, setConfigSaving] = useState(false);

  // Search & Filter
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ status:'', city:'', source:'', priority:'', syncStatus:'' });

  // Inline edit
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Bulk operations
  const [selected, setSelected] = useState([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ status:'', priority:'', source:'' });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const hdr = { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };

  const loadRecords = async () => {
    try {
      setLoading(true);
      const r = await axios.get(API+'/master-data/list', {
        ...hdr,
        params: { page, limit:15, search, ...filters }
      });
      setRecords(r.data.records||[]);
      setTotal(r.data.total||0);
      setPages(r.data.pages||1);
      setSelected([]);
    } catch(e) { setError(e.response?.data?.message||'Failed to load'); }
    finally { setLoading(false); }
  };

  const loadStats = async () => {
    try { const r = await axios.get(API+'/master-data/stats', hdr); setStats(r.data.stats); }
    catch(e) { console.error(e); }
  };

  const loadColumns = async () => {
    try { const r = await axios.get(API+'/master-data/config', hdr); setColumns(r.data.columns||[]); }
    catch(e) { console.error(e); }
  };

  const loadAllColumns = async () => {
    try { const r = await axios.get(API+'/master-data/config/all', hdr); setConfigColumns(r.data.columns||[]); }
    catch(e) { console.error(e); }
  };

  const loadHistory = async () => {
    try {
      const [uh, dh] = await Promise.all([
        axios.get(API+'/master-data/history/upload', hdr),
        axios.get(API+'/master-data/history/download', hdr),
      ]);
      setUploadHistory(uh.data.history||[]);
      setDownloadHistory(dh.data.history||[]);
    } catch(e) { console.error(e); }
  };

  const loadAudit = async () => {
    try { const r = await axios.get(API+'/master-data/audit', hdr); setAuditLogs(r.data.logs||[]); }
    catch(e) { console.error(e); }
  };

  useEffect(() => { loadStats(); loadColumns(); }, []);
  useEffect(() => { if(tab===0) loadRecords(); }, [tab, page, search, filters]);
  useEffect(() => { if(tab===2) loadHistory(); }, [tab]);
  useEffect(() => { if(tab===3&&isAdmin) loadAudit(); }, [tab]);

  // Inline edit handlers
  const startEdit = (record) => {
    setEditId(record._id);
    setEditForm({
      name: record.name, phone: record.phone, email: record.email||'',
      city: record.city||'', state: record.state||'', pincode: record.pincode||'',
      source: record.source||'', priority: record.priority||'medium',
      status: record.status||'active', notes: record.notes||'',
    });
  };

  const saveEdit = async (id) => {
    try {
      setEditSaving(true);
      await axios.put(API+'/master-data/'+id, editForm, hdr);
      setEditId(null);
      setSuccess('Record updated and synced to Pre-Leads!');
      loadRecords(); loadStats(); setTimeout(()=>setSuccess(''),3000);
    } catch(e) { setError(e.response?.data?.message||'Update failed'); }
    finally { setEditSaving(false); }
  };

  // Bulk operations
  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  const toggleSelectAll = () => setSelected(selected.length===records.length ? [] : records.map(r=>r._id));

  const handleBulkEdit = async () => {
    const updates = Object.fromEntries(Object.entries(bulkForm).filter(([,v])=>v!==''));
    if (!Object.keys(updates).length) return setError('Select at least one field to update');
    try {
      setBulkSaving(true);
      await Promise.all(selected.map(id => axios.put(API+'/master-data/'+id, updates, hdr)));
      setBulkEditOpen(false);
      setBulkForm({status:'',priority:'',source:''});
      setSuccess(selected.length+' records updated!');
      loadRecords(); loadStats(); setSelected([]); setTimeout(()=>setSuccess(''),3000);
    } catch(e) { setError('Bulk update failed'); }
    finally { setBulkSaving(false); }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selected.map(id => axios.delete(API+'/master-data/'+id, hdr)));
      setDeleteConfirmOpen(false);
      setSuccess(selected.length+' records deleted!');
      loadRecords(); loadStats(); setSelected([]); setTimeout(()=>setSuccess(''),3000);
    } catch(e) { setError('Bulk delete failed'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx','xls','csv'].includes(ext)) return setError('Only .xlsx, .xls and .csv files allowed');
    setUploading(true); setUploadProgress(10); setUploadResult(null); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      setUploadProgress(30);
      const r = await axios.post(API+'/master-data/upload', fd, {
        ...hdr, headers: { ...hdr.headers, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: p => setUploadProgress(Math.min(80, Math.round(p.loaded/p.total*80))),
      });
      setUploadProgress(100);
      setUploadResult(r.data);
      setSuccess('Upload complete! '+r.data.summary.inserted+' inserted, '+r.data.summary.updated+' updated');
      loadRecords(); loadStats(); loadHistory(); setTimeout(()=>setSuccess(''),5000);
    } catch(e) { setError(e.response?.data?.message||'Upload failed'); }
    finally { setUploading(false); setUploadProgress(0); fileRef.current.value=''; }
  };

  const handleDownload = async (format) => {
    try {
      const r = await axios.get(API+'/master-data/download', {
        ...hdr, params: { format, search, ...filters }, responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href=url; a.download='master_data.'+format; a.click();
      URL.revokeObjectURL(url);
      setSuccess('Download started!'); setTimeout(()=>setSuccess(''),3000); loadHistory();
    } catch(e) { setError('Download failed'); }
  };

  const handleTemplate = async () => {
    try {
      const r = await axios.get(API+'/master-data/template', {...hdr, responseType:'blob'});
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href=url; a.download='master_data_template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { setError('Failed to download template'); }
  };

  const downloadErrors = async (batchId) => {
    try {
      const r = await axios.get(API+'/master-data/errors/'+batchId, {...hdr, responseType:'blob'});
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href=url; a.download='error_report.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { setError('Failed to download error report'); }
  };

  const saveColumns = async () => {
    try {
      setConfigSaving(true);
      await axios.put(API+'/master-data/config', {columns:configColumns}, hdr);
      setSuccess('Column configuration saved!');
      loadColumns(); setTimeout(()=>setSuccess(''),3000);
    } catch(e) { setError('Failed to save config'); }
    finally { setConfigSaving(false); }
  };

  const addColumn = async () => {
    if(!newCol.label) return setError('Column label required');
    try {
      await axios.post(API+'/master-data/config/column', {
        ...newCol, options: newCol.options ? newCol.options.split(',').map(o=>o.trim()) : [],
      }, hdr);
      setAddColOpen(false); setNewCol({label:'',type:'text',required:false,options:''});
      setSuccess('Column added!'); loadColumns(); loadAllColumns(); setTimeout(()=>setSuccess(''),3000);
    } catch(e) { setError('Failed to add column'); }
  };

  const clearFilters = () => { setFilters({status:'',city:'',source:'',priority:'',syncStatus:''}); setSearch(''); setPage(1); };
  const activeFilters = Object.values(filters).filter(Boolean).length + (search?1:0);

  const EditCell = ({field, type='text', options=[]}) => (
    type==='select'
      ? <Select size="small" value={editForm[field]||''} onChange={e=>setEditForm({...editForm,[field]:e.target.value})} sx={{fontSize:11,height:24,minWidth:90}}>
          {options.map(o=><MenuItem key={o} value={o} sx={{fontSize:11}}>{o}</MenuItem>)}
        </Select>
      : <TextField size="small" value={editForm[field]||''} onChange={e=>setEditForm({...editForm,[field]:e.target.value})}
          inputProps={{style:{fontSize:11,padding:'2px 6px'}}} sx={{minWidth:80}}/>
  );

  return (
    <Box p={3}>
      <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={handleUpload}/>

      <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Master Data Management</Typography>
          <Typography variant="body2" color="text.secondary">Single source of truth — synced with Pre-Leads automatically</Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button variant="outlined" size="small" startIcon={<FileDownload/>} onClick={handleTemplate} sx={{borderColor:'#2196F3',color:'#2196F3',borderRadius:2,textTransform:'none'}}>Template</Button>
          <Button variant="outlined" size="small" startIcon={<Download/>} onClick={()=>handleDownload('xlsx')} sx={{borderColor:'#4CAF50',color:'#4CAF50',borderRadius:2,textTransform:'none'}}>XLSX</Button>
          <Button variant="outlined" size="small" startIcon={<Download/>} onClick={()=>handleDownload('csv')} sx={{borderColor:'#4CAF50',color:'#4CAF50',borderRadius:2,textTransform:'none'}}>CSV</Button>
          {isAdmin&&<Button variant="outlined" size="small" startIcon={<Edit/>} onClick={()=>{loadAllColumns();setConfigOpen(true);}} sx={{borderColor:'#9C27B0',color:'#9C27B0',borderRadius:2,textTransform:'none'}}>Columns</Button>}
          {canUpload&&<Button variant="contained" size="small" startIcon={<CloudUpload/>} onClick={()=>fileRef.current.click()} disabled={uploading} sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,textTransform:'none',fontWeight:600}}>{uploading?'Uploading...':'Upload'}</Button>}
        </Box>
      </Box>

      {error&&<Alert severity="error" sx={{mb:2}} onClose={()=>setError('')}>{error}</Alert>}
      {success&&<Alert severity="success" sx={{mb:2}} onClose={()=>setSuccess('')}>{success}</Alert>}

      {uploading&&<Card sx={{mb:2,borderRadius:2}}><CardContent sx={{pb:'12px !important'}}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <CircularProgress size={20} sx={{color:'#E8622A'}}/>
          <Typography variant="body2" fontWeight={600}>Processing upload... {uploadProgress}%</Typography>
        </Box>
        <LinearProgress variant="determinate" value={uploadProgress} sx={{borderRadius:2,height:6,'& .MuiLinearProgress-bar':{bgcolor:'#E8622A'}}}/>
      </CardContent></Card>}

      {uploadResult&&<Card sx={{mb:2,borderRadius:2,border:'1px solid '+(uploadResult.status==='completed'?'#4CAF5030':uploadResult.status==='partial'?'#FF980030':'#F4433630')}}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            {uploadResult.status==='completed'?<CheckCircle sx={{color:'#4CAF50'}}/>:uploadResult.status==='partial'?<Warning sx={{color:'#FF9800'}}/>:<Error sx={{color:'#F44336'}}/>}
            <Typography fontWeight={700}>Upload {uploadResult.status==='completed'?'Successful':uploadResult.status==='partial'?'Partially Successful':'Failed'}</Typography>
            <Button size="small" onClick={()=>setUploadResult(null)} sx={{ml:'auto',color:'#999'}}>Dismiss</Button>
          </Box>
          <Grid container spacing={1.5}>
            {[{l:'Total',v:uploadResult.summary?.total,c:'#2196F3'},{l:'Inserted',v:uploadResult.summary?.inserted,c:'#4CAF50'},{l:'Updated',v:uploadResult.summary?.updated,c:'#FF9800'},{l:'Failed',v:uploadResult.summary?.failed,c:'#F44336'},{l:'Synced',v:uploadResult.summary?.syncedToLeads,c:'#9C27B0'}].map(s=>(
              <Grid item xs key={s.l}><Box textAlign="center" sx={{bgcolor:s.c+'10',borderRadius:2,p:1}}>
                <Typography variant="h6" fontWeight={700} color={s.c}>{s.v??0}</Typography>
                <Typography variant="caption" color="text.secondary">{s.l}</Typography>
              </Box></Grid>
            ))}
          </Grid>
          {uploadResult.errors?.length>0&&<Box mt={2}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" fontWeight={600} color="error">Errors ({uploadResult.errors.length})</Typography>
              <Button size="small" startIcon={<FileDownload/>} onClick={()=>downloadErrors(uploadResult.batchId)} sx={{color:'#F44336',fontSize:11}}>Download Report</Button>
            </Box>
            <TableContainer sx={{maxHeight:150}}><Table size="small">
              <TableHead><TableRow sx={{'& th':{bgcolor:'#FFF3F3',fontSize:11,fontWeight:700}}}><TableCell>Row</TableCell><TableCell>Column</TableCell><TableCell>Error</TableCell></TableRow></TableHead>
              <TableBody>{uploadResult.errors.slice(0,10).map((e,i)=><TableRow key={i}><TableCell>{e.row}</TableCell><TableCell>{e.column}</TableCell><TableCell sx={{color:'#F44336',fontSize:11}}>{e.error}</TableCell></TableRow>)}</TableBody>
            </Table></TableContainer>
          </Box>}
        </CardContent>
      </Card>}

      {stats&&<Grid container spacing={2} mb={3}>
        {[].map(s=>(
          <Grid item xs={6} md={3} key={s.l}><Card sx={{borderLeft:'4px solid '+s.c,borderRadius:2}}><CardContent sx={{py:'12px !important'}}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box><Typography variant="caption" color="text.secondary">{s.l}</Typography><Typography variant="h5" fontWeight={700} color={s.c}>{s.v??0}</Typography></Box>
              <Avatar sx={{bgcolor:s.c+'20',color:s.c,width:40,height:40}}>{s.icon}</Avatar>
            </Box>
          </CardContent></Card></Grid>
        ))}
      </Grid>}

      <Paper sx={{borderRadius:2,mb:2}}>
        <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{'& .MuiTab-root':{fontWeight:600,textTransform:'none',fontSize:13}}}>
          <Tab label="📋 Records"/><Tab label="📤 Upload"/><Tab label="📜 History"/>{isAdmin&&<Tab label="🔍 Audit Trail"/>}
        </Tabs>
      </Paper>

      {/* RECORDS TAB */}
      {tab===0&&<Box>
        {/* Search + Filters */}
        <Box display="flex" gap={1.5} mb={1.5} flexWrap="wrap" alignItems="center">
          <TextField placeholder="Search name, phone, email, city..." value={search}
            onChange={e=>{setSearch(e.target.value);setPage(1);}} size="small" sx={{flex:1,minWidth:220}}
            InputProps={{startAdornment:<Typography sx={{mr:0.5,color:'#999',fontSize:14}}>🔍</Typography>}}/>
          <Button variant={showFilters?'contained':'outlined'} size="small" startIcon={<FilterList/>}
            onClick={()=>setShowFilters(!showFilters)}
            sx={{borderColor:'#2196F3',color:showFilters?'white':'#2196F3',bgcolor:showFilters?'#2196F3':'transparent',borderRadius:2,textTransform:'none',minWidth:110}}>
            Filters {activeFilters>0&&<Chip label={activeFilters} size="small" sx={{ml:0.5,height:16,bgcolor:'white',color:'#2196F3',fontSize:10}}/>}
          </Button>
          {activeFilters>0&&<Button size="small" onClick={clearFilters} sx={{color:'#F44336',textTransform:'none',fontSize:12}}>✕ Clear</Button>}
          <Button variant="outlined" startIcon={<Refresh/>} onClick={()=>{loadRecords();loadStats();}} size="small" sx={{borderColor:'#E8622A',color:'#E8622A',borderRadius:2,textTransform:'none'}}>Refresh</Button>
        </Box>

        {/* Advanced Filters Panel */}
        <Collapse in={showFilters}>
          <Card sx={{mb:1.5,borderRadius:2,border:'1px solid #2196F330',bgcolor:'#F8FBFF'}}>
            <CardContent sx={{py:'12px !important'}}>
              <Typography variant="body2" fontWeight={700} mb={1.5} color="#2196F3">🔧 Advanced Filters</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{fontSize:12}}>Status</InputLabel>
                    <Select value={filters.status} label="Status" onChange={e=>{setFilters({...filters,status:e.target.value});setPage(1);}}>
                      <MenuItem value=""><em>All</em></MenuItem>
                      <MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem><MenuItem value="duplicate">Duplicate</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{fontSize:12}}>Priority</InputLabel>
                    <Select value={filters.priority} label="Priority" onChange={e=>{setFilters({...filters,priority:e.target.value});setPage(1);}}>
                      <MenuItem value=""><em>All</em></MenuItem>
                      <MenuItem value="high">High</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="low">Low</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{fontSize:12}}>Source</InputLabel>
                    <Select value={filters.source} label="Source" onChange={e=>{setFilters({...filters,source:e.target.value});setPage(1);}}>
                      <MenuItem value=""><em>All</em></MenuItem>
                      {['website','referral','cold_call','campaign','walk_in','social_media'].map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{fontSize:12}}>Sync Status</InputLabel>
                    <Select value={filters.syncStatus} label="Sync Status" onChange={e=>{setFilters({...filters,syncStatus:e.target.value});setPage(1);}}>
                      <MenuItem value=""><em>All</em></MenuItem>
                      <MenuItem value="synced">Synced</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField fullWidth label="City" size="small" value={filters.city} onChange={e=>{setFilters({...filters,city:e.target.value});setPage(1);}} InputLabelProps={{sx:{fontSize:12}}}/>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>

        {/* Bulk Actions Bar */}
        {selected.length>0&&<Card sx={{mb:1.5,borderRadius:2,bgcolor:'#E8622A08',border:'1px solid #E8622A30'}}>
          <CardContent sx={{py:'10px !important',display:'flex',alignItems:'center',gap:2,flexWrap:'wrap'}}>
            <Typography fontWeight={700} color="#E8622A">{selected.length} record{selected.length>1?'s':''} selected</Typography>
            <Button size="small" variant="contained" startIcon={<BulkEdit/>} onClick={()=>setBulkEditOpen(true)}
              sx={{bgcolor:'#2196F3','&:hover':{bgcolor:'#1976D2'},borderRadius:2,textTransform:'none',fontWeight:600}}>
              Bulk Edit
            </Button>
            {isAdmin&&<Button size="small" variant="contained" color="error" startIcon={<Delete/>} onClick={()=>setDeleteConfirmOpen(true)}
              sx={{borderRadius:2,textTransform:'none',fontWeight:600}}>
              Delete Selected
            </Button>}
            <Button size="small" onClick={()=>setSelected([])} sx={{color:'#999',textTransform:'none'}}>Clear Selection</Button>
          </CardContent>
        </Card>}

        {/* Pagination top */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" color="text.secondary">
            {total} records {activeFilters>0&&<span style={{color:'#E8622A'}}>({activeFilters} filter{activeFilters>1?'s':''} active)</span>}
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <Button size="small" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>‹ Prev</Button>
            <Typography variant="body2" sx={{px:1}}>{page}/{pages}</Typography>
            <Button size="small" disabled={page>=pages} onClick={()=>setPage(p=>p+1)}>Next ›</Button>
          </Box>
        </Box>

        {loading?<Box display="flex" justifyContent="center" mt={4}><CircularProgress sx={{color:'#E8622A'}}/></Box>:(
          <TableContainer component={Paper} elevation={1} sx={{borderRadius:2}}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{'& th':{bgcolor:'#F5F7FA',fontWeight:700,fontSize:11,color:'#555'}}}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={selected.length===records.length&&records.length>0}
                      indeterminate={selected.length>0&&selected.length<records.length}
                      onChange={toggleSelectAll}/>
                  </TableCell>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell><TableCell>Phone</TableCell><TableCell>Alt Phone</TableCell>
                  <TableCell>City</TableCell><TableCell>State</TableCell>
                  <TableCell>Source</TableCell><TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell><TableCell>Sync</TableCell>
                  <TableCell>Pre-Lead</TableCell><TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length===0
                  ?<TableRow><TableCell colSpan={14} align="center" sx={{py:6,color:'text.secondary'}}>No records found. Upload a file or adjust filters.</TableCell></TableRow>
                  :records.map((r,i)=>{
                    const isEditing = editId===r._id;
                    return (
                      <TableRow key={r._id} hover selected={selected.includes(r._id)}
                        sx={{'&:hover':{bgcolor:'#FFF8F5'},bgcolor:selected.includes(r._id)?'#FFF3EE':isEditing?'#FFFDE7':'inherit'}}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={selected.includes(r._id)} onChange={()=>toggleSelect(r._id)}/>
                        </TableCell>
                        <TableCell sx={{color:'#999',fontSize:11}}>{(page-1)*15+i+1}</TableCell>
                        <TableCell sx={{minWidth:120}}>
                          {isEditing ? <EditCell field="name"/> : <Typography variant="body2" fontWeight={500}>{r.name}</Typography>}
                        </TableCell>
                        <TableCell sx={{minWidth:110}}>
                          {isEditing ? <EditCell field="phone"/> : <Typography sx={{fontFamily:'monospace',fontSize:12}}>{r.phone}</Typography>}
                        </TableCell>
                        <TableCell sx={{minWidth:110}}>
                          {isEditing ? <EditCell field="alternatePhone"/> : <Typography sx={{fontFamily:'monospace',fontSize:12,color:'#888'}}>{r.alternatePhone||'—'}</Typography>}
                        </TableCell>

                        <TableCell sx={{minWidth:90}}>
                          {isEditing ? <EditCell field="city"/> : <Typography sx={{fontSize:12}}>{r.city||'—'}</Typography>}
                        </TableCell>
                        <TableCell sx={{minWidth:90}}>
                          {isEditing ? <EditCell field="state"/> : <Typography sx={{fontSize:12}}>{r.state||'—'}</Typography>}
                        </TableCell>
                        <TableCell>
                          {isEditing
                            ? <EditCell field="source" type="select" options={['website','referral','cold_call','campaign','walk_in','social_media']}/>
                            : <Chip label={r.source||'other'} size="small" sx={{fontSize:10,height:20}}/>}
                        </TableCell>
                        <TableCell>
                          {isEditing
                            ? <EditCell field="priority" type="select" options={['low','medium','high']}/>
                            : <Chip label={r.priority||'medium'} size="small" sx={{bgcolor:(PRIORITY_COLORS[r.priority]||'#FF9800')+'20',color:PRIORITY_COLORS[r.priority]||'#FF9800',fontSize:10,height:20}}/>}
                        </TableCell>
                        <TableCell>
                          {isEditing
                            ? <EditCell field="status" type="select" options={['active','inactive','duplicate']}/>
                            : <Chip label={r.status||'active'} size="small" sx={{bgcolor:r.status==='active'?'#4CAF5020':'#F4433620',color:r.status==='active'?'#4CAF50':'#F44336',fontSize:10,height:20}}/>}
                        </TableCell>
                        <TableCell>
                          <Chip label={r.syncStatus} size="small" sx={{bgcolor:(STATUS_COLORS[r.syncStatus]||'#9E9E9E')+'20',color:STATUS_COLORS[r.syncStatus]||'#9E9E9E',fontSize:10,height:20}}/>
                        </TableCell>
                        <TableCell>
                          <Chip label={r.leadRef?'Linked':'Not linked'} size="small" sx={{bgcolor:r.leadRef?'#4CAF5020':'#9E9E9E20',color:r.leadRef?'#4CAF50':'#9E9E9E',fontSize:10,height:20}}/>
                        </TableCell>
                        <TableCell>
                          {isEditing
                            ? <Box display="flex" gap={0.5}>
                                <Tooltip title="Save"><IconButton size="small" onClick={()=>saveEdit(r._id)} disabled={editSaving} sx={{color:'#4CAF50'}}>
                                  {editSaving?<CircularProgress size={14}/>:<Save sx={{fontSize:15}}/>}
                                </IconButton></Tooltip>
                                <Tooltip title="Cancel"><IconButton size="small" onClick={()=>setEditId(null)} sx={{color:'#F44336'}}><Cancel sx={{fontSize:15}}/></IconButton></Tooltip>
                              </Box>
                            : <Box display="flex" gap={0.5}>
                                <Tooltip title="Edit"><IconButton size="small" onClick={()=>startEdit(r)} sx={{color:'#2196F3'}}><Edit sx={{fontSize:15}}/></IconButton></Tooltip>
                                {isAdmin&&<Tooltip title="Delete"><IconButton size="small" onClick={async()=>{await axios.delete(API+'/master-data/'+r._id,hdr);loadRecords();loadStats();}} sx={{color:'#F44336'}}><Delete sx={{fontSize:15}}/></IconButton></Tooltip>}
                              </Box>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>}

      {/* UPLOAD TAB */}
      {tab===1&&<Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{borderRadius:3,border:'2px dashed #E8622A40'}}>
            <CardContent sx={{textAlign:'center',py:5}}>
              <CloudUpload sx={{fontSize:64,color:'#E8622A',mb:2}}/>
              <Typography variant="h6" fontWeight={700} mb={1}>Upload Master Data</Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>Supports .xlsx, .xls and .csv up to 10MB.<br/>Auto-syncs with Pre-Leads.</Typography>
              <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                <Button variant="outlined" startIcon={<FileDownload/>} onClick={handleTemplate} sx={{borderColor:'#2196F3',color:'#2196F3',borderRadius:2,textTransform:'none'}}>Download Template</Button>
                <Button variant="contained" startIcon={<CloudUpload/>} onClick={()=>fileRef.current.click()} disabled={uploading}
                  sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,textTransform:'none',fontWeight:600}}>
                  {uploading?<><CircularProgress size={18} sx={{color:'white',mr:1}}/>Uploading...</>:'Choose File & Upload'}
                </Button>
              </Box>
              {uploading&&<Box mt={3}><LinearProgress variant="determinate" value={uploadProgress} sx={{borderRadius:2,height:8,'& .MuiLinearProgress-bar':{bgcolor:'#E8622A'}}}/></Box>}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{borderRadius:3}}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>Template Columns</Typography>
              <Divider sx={{mb:2}}/>
              {columns.map(col=>(
                <Box key={col.key} display="flex" alignItems="center" gap={1} py={0.5}>
                  <Chip label={col.required?'Required':'Optional'} size="small" sx={{width:72,bgcolor:col.required?'#F4433620':'#4CAF5020',color:col.required?'#F44336':'#4CAF50',fontSize:10,fontWeight:600}}/>
                  <Typography variant="body2" fontWeight={600} sx={{width:90,fontFamily:'monospace',fontSize:12}}>{col.label}</Typography>
                  <Chip label={col.type} size="small" sx={{fontSize:10,height:18,bgcolor:'#E8622A10',color:'#E8622A'}}/>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>}

      {/* HISTORY TAB */}
      {tab===2&&<Box>
        <Typography variant="h6" fontWeight={700} mb={2}>Upload History</Typography>
        <TableContainer component={Paper} elevation={1} sx={{borderRadius:2,mb:3}}>
          <Table size="small">
            <TableHead><TableRow sx={{'& th':{bgcolor:'#F5F7FA',fontWeight:700,fontSize:12}}}>
              <TableCell>File</TableCell><TableCell>By</TableCell><TableCell>Date</TableCell>
              <TableCell>Total</TableCell><TableCell>Inserted</TableCell><TableCell>Updated</TableCell>
              <TableCell>Failed</TableCell><TableCell>Synced</TableCell><TableCell>Status</TableCell><TableCell>Action</TableCell>
            </TableRow></TableHead>
            <TableBody>{uploadHistory.length===0
              ?<TableRow><TableCell colSpan={10} align="center" sx={{py:4,color:'text.secondary'}}>No history</TableCell></TableRow>
              :uploadHistory.map(h=>(
              <TableRow key={h._id} hover>
                <TableCell sx={{fontSize:11,maxWidth:130,overflow:'hidden',textOverflow:'ellipsis'}}>{h.fileName}</TableCell>
                <TableCell sx={{fontSize:11}}>{h.uploadedBy?.name||'—'}</TableCell>
                <TableCell sx={{fontSize:11,color:'#999'}}>{new Date(h.createdAt).toLocaleString()}</TableCell>
                <TableCell><Chip label={h.totalRecords} size="small" sx={{bgcolor:'#2196F320',color:'#2196F3',fontSize:10}}/></TableCell>
                <TableCell><Chip label={h.inserted} size="small" sx={{bgcolor:'#4CAF5020',color:'#4CAF50',fontSize:10}}/></TableCell>
                <TableCell><Chip label={h.updated} size="small" sx={{bgcolor:'#FF980020',color:'#FF9800',fontSize:10}}/></TableCell>
                <TableCell><Chip label={h.failed} size="small" sx={{bgcolor:'#F4433620',color:'#F44336',fontSize:10}}/></TableCell>
                <TableCell><Chip label={h.syncedToLeads||0} size="small" sx={{bgcolor:'#9C27B020',color:'#9C27B0',fontSize:10}}/></TableCell>
                <TableCell><Chip label={h.status} size="small" sx={{bgcolor:(UPLOAD_STATUS_COLORS[h.status]||'#9E9E9E')+'20',color:UPLOAD_STATUS_COLORS[h.status]||'#9E9E9E',fontSize:10,fontWeight:600}}/></TableCell>
                <TableCell>{h.failed>0&&<Tooltip title="Error Report"><IconButton size="small" onClick={()=>downloadErrors(h.batchId)} sx={{color:'#F44336'}}><FileDownload sx={{fontSize:15}}/></IconButton></Tooltip>}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </TableContainer>
        <Typography variant="h6" fontWeight={700} mb={2}>Download History</Typography>
        <TableContainer component={Paper} elevation={1} sx={{borderRadius:2}}>
          <Table size="small">
            <TableHead><TableRow sx={{'& th':{bgcolor:'#F5F7FA',fontWeight:700,fontSize:12}}}>
              <TableCell>File</TableCell><TableCell>By</TableCell><TableCell>Date</TableCell><TableCell>Records</TableCell><TableCell>Format</TableCell>
            </TableRow></TableHead>
            <TableBody>{downloadHistory.length===0
              ?<TableRow><TableCell colSpan={5} align="center" sx={{py:4,color:'text.secondary'}}>No history</TableCell></TableRow>
              :downloadHistory.map(h=>(
              <TableRow key={h._id} hover>
                <TableCell sx={{fontSize:11}}>{h.fileName}</TableCell>
                <TableCell sx={{fontSize:11}}>{h.downloadedBy?.name||'—'}</TableCell>
                <TableCell sx={{fontSize:11,color:'#999'}}>{new Date(h.createdAt).toLocaleString()}</TableCell>
                <TableCell><Chip label={h.totalRecords} size="small" sx={{bgcolor:'#2196F320',color:'#2196F3',fontSize:10}}/></TableCell>
                <TableCell><Chip label={h.fileType?.toUpperCase()} size="small" sx={{bgcolor:'#4CAF5020',color:'#4CAF50',fontSize:10}}/></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </TableContainer>
      </Box>}

      {/* AUDIT TAB */}
      {isAdmin&&tab===3&&<Box>
        <Typography variant="h6" fontWeight={700} mb={2}>Audit Trail</Typography>
        <TableContainer component={Paper} elevation={1} sx={{borderRadius:2}}>
          <Table size="small">
            <TableHead><TableRow sx={{'& th':{bgcolor:'#F5F7FA',fontWeight:700,fontSize:12}}}>
              <TableCell>User</TableCell><TableCell>Action</TableCell><TableCell>File</TableCell>
              <TableCell>Records</TableCell><TableCell>IP</TableCell><TableCell>Status</TableCell><TableCell>Time</TableCell>
            </TableRow></TableHead>
            <TableBody>{auditLogs.length===0
              ?<TableRow><TableCell colSpan={7} align="center" sx={{py:4,color:'text.secondary'}}>No logs</TableCell></TableRow>
              :auditLogs.map(l=>(
              <TableRow key={l._id} hover>
                <TableCell sx={{fontSize:11,fontWeight:500}}>{l.userName||'—'}</TableCell>
                <TableCell><Chip label={l.action} size="small" sx={{fontSize:9,height:20,bgcolor:'#E8622A15',color:'#E8622A'}}/></TableCell>
                <TableCell sx={{fontSize:11,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis'}}>{l.fileName||'—'}</TableCell>
                <TableCell sx={{fontSize:11}}>{l.recordCount||'—'}</TableCell>
                <TableCell sx={{fontSize:10,color:'#999',fontFamily:'monospace'}}>{l.ipAddress||'—'}</TableCell>
                <TableCell><Chip label={l.status} size="small" sx={{bgcolor:l.status==='success'?'#4CAF5020':'#F4433620',color:l.status==='success'?'#4CAF50':'#F44336',fontSize:10}}/></TableCell>
                <TableCell sx={{fontSize:11,color:'#999'}}>{new Date(l.createdAt).toLocaleString()}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </TableContainer>
      </Box>}

      {/* Configure Columns Dialog */}
      <Dialog open={configOpen} onClose={()=>setConfigOpen(false)} maxWidth="md" fullWidth PaperProps={{sx:{borderRadius:3}}}>
        <DialogTitle sx={{display:'flex',alignItems:'center',justifyContent:'space-between',pb:1,borderBottom:'1px solid #eee'}}>
          <Box><Typography fontWeight={700} fontSize={18}>Configure Columns</Typography>
            <Typography variant="caption" color="text.secondary">Toggle required, rename, reorder, add custom columns</Typography>
          </Box>
          <Button variant="contained" size="small" startIcon={<Add/>} onClick={()=>setAddColOpen(true)} sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,textTransform:'none',fontWeight:600}}>Add Column</Button>
        </DialogTitle>
        <DialogContent sx={{pt:2}}>
          <Alert severity="info" sx={{mb:2}}>Changes update the upload template and validation rules.</Alert>
          <TableContainer><Table size="small">
            <TableHead><TableRow sx={{'& th':{bgcolor:'#F5F7FA',fontWeight:700,fontSize:12}}}>
              <TableCell>Order</TableCell><TableCell>Key</TableCell><TableCell>Label</TableCell>
              <TableCell>Type</TableCell><TableCell>Required</TableCell><TableCell>Visible</TableCell><TableCell>Lead Field</TableCell>
            </TableRow></TableHead>
            <TableBody>{configColumns.map((col,i)=>(
              <TableRow key={col.key||i} hover>
                <TableCell><Box display="flex" gap={0.3}>
                  <IconButton size="small" disabled={i===0} onClick={()=>{const nc=[...configColumns];[nc[i-1],nc[i]]=[nc[i],nc[i-1]];nc.forEach((c,idx)=>c.order=idx+1);setConfigColumns([...nc]);}}>↑</IconButton>
                  <IconButton size="small" disabled={i===configColumns.length-1} onClick={()=>{const nc=[...configColumns];[nc[i],nc[i+1]]=[nc[i+1],nc[i]];nc.forEach((c,idx)=>c.order=idx+1);setConfigColumns([...nc]);}}>↓</IconButton>
                </Box></TableCell>
                <TableCell><Typography sx={{fontFamily:'monospace',fontSize:11,color:'#E8622A'}}>{col.key}</Typography></TableCell>
                <TableCell><TextField size="small" value={col.label} onChange={e=>{const nc=[...configColumns];nc[i]={...nc[i],label:e.target.value};setConfigColumns(nc);}} sx={{width:120}} inputProps={{style:{fontSize:12,padding:'4px 8px'}}}/></TableCell>
                <TableCell><Select size="small" value={col.type||'text'} onChange={e=>{const nc=[...configColumns];nc[i]={...nc[i],type:e.target.value};setConfigColumns(nc);}} sx={{fontSize:11,height:28,minWidth:80}}>
                  {['text','number','email','phone','date','select'].map(t=><MenuItem key={t} value={t} sx={{fontSize:11}}>{t}</MenuItem>)}
                </Select></TableCell>
                <TableCell><Chip label={col.required?'Required':'Optional'} size="small" onClick={()=>{const nc=[...configColumns];nc[i]={...nc[i],required:!nc[i].required};setConfigColumns(nc);}} sx={{cursor:'pointer',bgcolor:col.required?'#F4433620':'#4CAF5020',color:col.required?'#F44336':'#4CAF50',fontWeight:600,fontSize:10}}/></TableCell>
                <TableCell><Chip label={col.active?'Active':'Hidden'} size="small" onClick={()=>{const nc=[...configColumns];nc[i]={...nc[i],active:!nc[i].active};setConfigColumns(nc);}} sx={{cursor:'pointer',bgcolor:col.active?'#2196F320':'#9E9E9E20',color:col.active?'#2196F3':'#9E9E9E',fontWeight:600,fontSize:10}}/></TableCell>
                <TableCell sx={{fontSize:11,color:'#9C27B0',fontFamily:'monospace'}}>{col.mapToLead||'—'}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></TableContainer>
        </DialogContent>
        <DialogActions sx={{p:2}}>
          <Button onClick={()=>setConfigOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveColumns} disabled={configSaving}
            sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,textTransform:'none',fontWeight:600}}>
            {configSaving?<CircularProgress size={18} sx={{color:'white'}}/>:'Save Configuration'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Column Dialog */}
      <Dialog open={addColOpen} onClose={()=>setAddColOpen(false)} maxWidth="xs" fullWidth PaperProps={{sx:{borderRadius:3}}}>
        <DialogTitle fontWeight={700}>Add Custom Column</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField fullWidth label="Column Label *" value={newCol.label} onChange={e=>setNewCol({...newCol,label:e.target.value})} size="small" helperText="e.g. Company Name, Budget"/>
            <FormControl fullWidth size="small"><InputLabel>Data Type</InputLabel>
              <Select value={newCol.type} label="Data Type" onChange={e=>setNewCol({...newCol,type:e.target.value})}>
                {['text','number','email','phone','date','select'].map(t=><MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            {newCol.type==='select'&&<TextField fullWidth label="Options (comma separated)" value={newCol.options} onChange={e=>setNewCol({...newCol,options:e.target.value})} size="small" helperText="e.g. Yes, No, Maybe"/>}
            <FormControl fullWidth size="small"><InputLabel>Required?</InputLabel>
              <Select value={newCol.required} label="Required?" onChange={e=>setNewCol({...newCol,required:e.target.value})}>
                <MenuItem value={false}>Optional</MenuItem><MenuItem value={true}>Required</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{p:2}}>
          <Button onClick={()=>setAddColOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={addColumn} sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,textTransform:'none',fontWeight:600}}>Add Column</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditOpen} onClose={()=>setBulkEditOpen(false)} maxWidth="xs" fullWidth PaperProps={{sx:{borderRadius:3}}}>
        <DialogTitle fontWeight={700}>Bulk Edit — {selected.length} Records</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{mb:2,mt:1}}>Only filled fields will be updated. Leave blank to keep existing values.</Alert>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth size="small"><InputLabel>Status</InputLabel>
              <Select value={bulkForm.status} label="Status" onChange={e=>setBulkForm({...bulkForm,status:e.target.value})}>
                <MenuItem value=""><em>— Keep existing —</em></MenuItem>
                <MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem><MenuItem value="duplicate">Duplicate</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small"><InputLabel>Priority</InputLabel>
              <Select value={bulkForm.priority} label="Priority" onChange={e=>setBulkForm({...bulkForm,priority:e.target.value})}>
                <MenuItem value=""><em>— Keep existing —</em></MenuItem>
                <MenuItem value="high">High</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small"><InputLabel>Source</InputLabel>
              <Select value={bulkForm.source} label="Source" onChange={e=>setBulkForm({...bulkForm,source:e.target.value})}>
                <MenuItem value=""><em>— Keep existing —</em></MenuItem>
                {['website','referral','cold_call','campaign','walk_in','social_media'].map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{p:2}}>
          <Button onClick={()=>setBulkEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkEdit} disabled={bulkSaving}
            sx={{bgcolor:'#2196F3','&:hover':{bgcolor:'#1976D2'},borderRadius:2,textTransform:'none',fontWeight:600}}>
            {bulkSaving?<CircularProgress size={18} sx={{color:'white'}}/>:'Update '+selected.length+' Records'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteConfirmOpen} onClose={()=>setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{sx:{borderRadius:3}}}>
        <DialogTitle fontWeight={700} color="error">Confirm Delete</DialogTitle>
        <DialogContent><Typography>Delete <strong>{selected.length} selected records</strong>? This cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{p:2}}>
          <Button onClick={()=>setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleBulkDelete} sx={{borderRadius:2,textTransform:'none',fontWeight:600}}>
            Delete {selected.length} Records
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
